<template>
  <!-- Backdrop -->
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open && !effectivePinned" class="fixed inset-0 z-40 bg-black/30" @click="close()" />
    </Transition>
  </Teleport>

  <!-- Drawer panel -->
  <Transition :name="effectivePinned ? 'drawer-push' : 'status-slide'">
    <div
      v-if="open"
      class="glass-surface flex flex-col bg-white dark:bg-gray-800 overflow-hidden pt-safe pb-safe"
      :class="[
        effectivePinned
          ? 'flex-shrink-0 h-screen sticky top-0 border-l border-gray-200 dark:border-gray-700 shadow-lg'
          : 'fixed z-50 border border-gray-200 dark:border-gray-700 shadow-2xl',
        !effectivePinned && !isMobile ? 'rounded-2xl' : '',
      ]"
      :style="panelStyle">
      <!-- Header -->
      <div
        class="glass-header flex items-center gap-2.5 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 flex-shrink-0">
        <div class="p-1.5 rounded-lg flex-shrink-0" :class="severityHeaderBg">
          <span class="material-symbols-outlined text-white" style="font-size: 18px">{{ severityHeaderIcon }}</span>
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
            {{ tt('System Status', 'Stanje sistema') }}
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{
              systemStatusStore.totalCount === 0
                ? tt('All clear', 'Vse v redu')
                : tt(
                    `${systemStatusStore.totalCount} active alert${systemStatusStore.totalCount !== 1 ? 's' : ''}`,
                    `${systemStatusStore.totalCount} ${slCount(systemStatusStore.totalCount, 'aktivno opozorilo', 'aktivni opozorili', 'aktivna opozorila', 'aktivnih opozoril')}`,
                  )
            }}
          </p>
        </div>
        <button
          v-if="!isMobile"
          @click="ui.toggleStatusDrawerPin()"
          class="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 flex-shrink-0"
          :title="effectivePinned ? tt('Unpin', 'Odpni') : tt('Pin', 'Pripni')">
          <span class="material-symbols-outlined text-lg">{{ effectivePinned ? 'push_pin' : 'keep' }}</span>
        </button>
        <button
          v-if="!effectivePinned"
          @click="close()"
          class="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 flex-shrink-0">
          <span class="material-symbols-outlined text-lg">close</span>
        </button>
      </div>

      <!-- Filter bar -->
      <div
        class="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
        <div class="flex gap-1.5">
          <button
            v-for="f in statusFilters"
            :key="f.value"
            @click="statusFilter = f.value"
            :class="[
              'px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors',
              statusFilter === f.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200',
            ]">
            {{ f.label }}
          </button>
        </div>
        <button
          @click="systemStatusStore.fetchStatusValues()"
          :title="tt('Refresh', 'Osveži')"
          class="p-1 rounded text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <span class="material-symbols-outlined text-sm">refresh</span>
        </button>
      </div>

      <!-- Scrollable content -->
      <div class="flex-1 overflow-y-auto min-h-0">
        <!-- Active Alerts Section -->
        <div v-if="filteredStatusItems.length > 0">
          <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-4 pt-3 pb-1">
            {{ tt('Active Alerts', 'Aktivna opozorila') }}
          </p>
          <div class="divide-y divide-gray-100 dark:divide-gray-700">
            <div
              v-for="item in filteredStatusItems"
              :key="item.valueId"
              @click="openDeviceModal(item.deviceId)"
              :class="[
                'flex items-start gap-3 px-3 py-3 cursor-pointer transition-colors group hover:bg-gray-50 dark:hover:bg-gray-700/40',
                statusRowClass(item.notificationLevel),
              ]">
              <div :class="['p-1.5 rounded-lg flex-shrink-0 mt-0.5', statusLevelBg(item.notificationLevel)]">
                <span class="material-symbols-outlined text-white" style="font-size: 14px">{{
                  statusLevelIcon(item.notificationLevel)
                }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-white leading-snug truncate">
                  {{ item.deviceName }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                  {{ item.notificationMessage || item.valueName }}
                </p>
                <p v-if="item.lastUpdated" class="text-[10px] text-gray-400 mt-0.5">
                  {{ formatTime(item.lastUpdated) }}
                </p>
              </div>
              <span
                class="material-symbols-outlined text-sm text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1"
                >chevron_right</span
              >
            </div>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-10">
          <span class="material-symbols-outlined text-4xl text-green-400 dark:text-green-600">check_circle</span>
          <p class="mt-2 text-sm text-gray-500">{{ tt('All systems normal', 'Vsi sistemi delujejo normalno') }}</p>
        </div>

        <!-- Event Log Section -->
        <div class="border-t border-gray-200 dark:border-gray-700 mt-2">
          <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-4 pt-3 pb-1">
            {{ tt('Event Log', 'Dnevnik dogodkov') }}
          </p>
          <div v-if="systemStatusStore.loadingEvents" class="flex justify-center py-6">
            <div class="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <div v-else-if="systemStatusStore.eventLog.length === 0" class="px-4 py-6 text-center">
            <p class="text-xs text-gray-400">{{ tt('No recent events', 'Ni nedavnih dogodkov') }}</p>
          </div>
          <div v-else class="divide-y divide-gray-100 dark:divide-gray-700">
            <div
              v-for="evt in systemStatusStore.eventLog"
              :key="evt.id"
              :class="[
                'flex items-start gap-2.5 px-3 py-2.5 text-xs group',
                evt.deviceId ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors' : '',
              ]"
              @click="evt.deviceId && openDeviceModal(evt.deviceId)">
              <span
                :class="['material-symbols-outlined flex-shrink-0 mt-0.5', eventSeverityColor(evt.severity)]"
                style="font-size: 14px"
                >{{ eventSeverityIcon(evt.severity) }}</span
              >
              <div class="flex-1 min-w-0">
                <p class="text-gray-700 dark:text-gray-300 leading-snug">{{ evt.message }}</p>
                <p class="text-[10px] text-gray-400 mt-0.5">{{ formatTime(evt.createdAt) }}</p>
              </div>
              <span
                v-if="evt.deviceId"
                class="material-symbols-outlined text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
                style="font-size: 14px"
                >chevron_right</span
              >
            </div>
          </div>
          <button
            v-if="systemStatusStore.eventLog.length >= 20"
            @click="systemStatusStore.loadMoreEvents()"
            class="w-full py-2 text-xs text-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            {{ tt('Load more', 'Naloži več') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Device Control Modal + the settings sheet its gear opens. The gear emits
       `open-settings` first and `close` right after, so `closeControlModal`
       must not let the device go when settings just took over — the card
       surfaces' own pattern (`DeviceIconCard.vue`). -->
  <Teleport to="body">
    <DeviceControlModal
      v-if="showControlModal && selectedDevice"
      :device="selectedDevice"
      :integration-name="getIntegrationForDevice(selectedDevice.integrationId)"
      @close="closeControlModal"
      @open-settings="openDeviceSettings"
      @view-stream="() => {}"
      @view-details="() => {}"
      @delete="() => {}" />
    <DeviceSettingsModal
      v-if="showSettingsModal && selectedDevice"
      :device="selectedDevice"
      @close="
        showSettingsModal = false;
        selectedDevice = null;
      " />
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useUIStore } from '../stores/ui';
import { useSystemStatusStore } from '../stores/systemStatus';
import { useEntitiesStore } from '../stores/entities';
import DeviceControlModal from './DeviceControlModal.vue';
import DeviceSettingsModal from './DeviceSettingsModalNew.vue';
import { tt } from '../utils/consoleText';
import { formatDate } from '../utils/formatDate';
import type { Device } from '@/types';

const ui = useUIStore();
const systemStatusStore = useSystemStatusStore();
const entitiesStore = useEntitiesStore();

const selectedDevice = ref<Device | null>(null);
const showControlModal = ref(false);
const showSettingsModal = ref(false);

// ── Open / close ──────────────────────────────────────────────────────────────
const open = computed(() => ui.statusDrawerOpen);

function close() {
  ui.closeStatusDrawer();
}

// ── Responsive ────────────────────────────────────────────────────────────────
const isMobile = ref(typeof window !== 'undefined' && window.innerWidth < 768);
function onResize() {
  isMobile.value = window.innerWidth < 768;
}
onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const effectivePinned = computed(() => !isMobile.value && ui.statusDrawerPinned);

const panelStyle = computed(() => {
  if (effectivePinned.value) return { width: '360px' };
  if (isMobile.value) return { top: '0', right: '0', bottom: '0', left: '0' };
  return { top: '60px', right: '16px', bottom: '16px', width: '360px' };
});

// ── Header severity ───────────────────────────────────────────────────────────
const severityHeaderBg = computed(() => {
  const s = systemStatusStore.highestSeverity;
  if (s === 'error') return 'bg-red-500';
  if (s === 'warning') return 'bg-yellow-500';
  if (s === 'alert') return 'bg-blue-500';
  return 'bg-green-500';
});
const severityHeaderIcon = computed(() => {
  const s = systemStatusStore.highestSeverity;
  if (s === 'error') return 'error';
  if (s === 'warning') return 'warning';
  if (s === 'alert') return 'info';
  return 'check_circle';
});

// ── Filter ────────────────────────────────────────────────────────────────────
const statusFilter = ref<'all' | 'error' | 'warning' | 'alert'>('all');
// Read through `tt()` inside the computed, so the chips follow a language switch.
const statusFilters = computed(() => [
  { value: 'all' as const, label: tt('All', 'Vse') },
  { value: 'error' as const, label: tt('Errors', 'Napake') },
  { value: 'warning' as const, label: tt('Warnings', 'Opozorila') },
  { value: 'alert' as const, label: tt('Alerts', 'Obvestila') },
]);
const filteredStatusItems = computed(() => {
  const items = systemStatusStore.statusItems;
  if (statusFilter.value === 'all') return items;
  return items.filter((i) => i.notificationLevel === statusFilter.value);
});

// ── Helpers ───────────────────────────────────────────────────────────────────
/**
 * Slovene agrees with the count (1 aktivno opozorilo, 2 aktivni opozorili,
 * 3 aktivna opozorila, 5 aktivnih opozoril); the last two digits decide, as
 * the language does. The same helper the user drawer carries.
 */
function slCount(n: number, one: string, two: string, few: string, many: string): string {
  const lastTwo = n % 100;
  if (lastTwo === 1) return one;
  if (lastTwo === 2) return two;
  if (lastTwo === 3 || lastTwo === 4) return few;
  return many;
}

function statusLevelIcon(level: string): string {
  if (level === 'error') return 'error';
  if (level === 'warning') return 'warning';
  return 'info';
}
function statusLevelBg(level: string): string {
  if (level === 'error') return 'bg-red-500';
  if (level === 'warning') return 'bg-yellow-500';
  return 'bg-blue-500';
}

/** Animated row class for active alert items. */
function statusRowClass(level: string): string {
  if (level === 'error') return 'status-row--error';
  if (level === 'warning') return 'status-row--warning';
  return '';
}
function openDeviceModal(deviceId: string) {
  const device = entitiesStore.devices.find((d: Device) => d.id === deviceId);
  if (!device) return;
  selectedDevice.value = device;
  showSettingsModal.value = false;
  showControlModal.value = true;
}

/**
 * The control sheet's gear: swap it for the settings sheet instead of only
 * closing, so a device opened from the event log behaves like one opened from
 * a dashboard card. The gear emits `open-settings` before `close`, so the
 * close handler below only lets the device go when settings did not take over.
 */
function openDeviceSettings() {
  showControlModal.value = false;
  showSettingsModal.value = true;
}

function closeControlModal() {
  showControlModal.value = false;
  if (!showSettingsModal.value) selectedDevice.value = null;
}

function getIntegrationForDevice(integrationId?: string): string {
  if (!integrationId) return '';
  return entitiesStore.integrations.find((i) => i.id === integrationId)?.integrationName ?? '';
}
function eventSeverityColor(severity: string): string {
  if (severity === 'error' || severity === 'critical') return 'text-red-500';
  if (severity === 'warning') return 'text-yellow-500';
  return 'text-blue-500';
}
function eventSeverityIcon(severity: string): string {
  if (severity === 'error' || severity === 'critical') return 'error';
  if (severity === 'warning') return 'warning';
  return 'info';
}
function formatTime(dateStr: string) {
  const date = new Date(dateStr);
  const m = Math.floor((Date.now() - date.getTime()) / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  // `pred 5 min` / `pred 2 h` / `pred 3 d` — the catalogue's own ago style.
  if (m < 1) return tt('Just now', 'Pravkar');
  if (m < 60) return tt(`${m}m ago`, `pred ${m} min`);
  if (h < 24) return tt(`${h}h ago`, `pred ${h} h`);
  if (d < 7) return tt(`${d}d ago`, `pred ${d} d`);
  return formatDate(date, { month: 'short', day: 'numeric' });
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────
watch(open, (isOpen) => {
  if (isOpen) systemStatusStore.initialize();
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.status-slide-enter-active,
.status-slide-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.2s;
}
.status-slide-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.status-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.drawer-push-enter-active,
.drawer-push-leave-active {
  transition: width 0.25s ease;
  overflow: hidden;
}
.drawer-push-enter-from,
.drawer-push-leave-to {
  width: 0 !important;
}

/* Active alert rows pulse with their severity color */
.status-row--error {
  animation: status-alarm-pulse 1.1s ease-in-out infinite;
}
.status-row--warning {
  animation: status-warn-pulse 1.8s ease-in-out infinite;
}
@keyframes status-alarm-pulse {
  0%,
  100% {
    background-color: rgba(239, 68, 68, 0);
  }
  50% {
    background-color: rgba(239, 68, 68, 0.16);
  }
}
@keyframes status-warn-pulse {
  0%,
  100% {
    background-color: rgba(245, 158, 11, 0);
  }
  50% {
    background-color: rgba(245, 158, 11, 0.14);
  }
}
</style>
